import { supabase } from '@/integrations/supabase/client';
import { API_URL } from '@/lib/apiClient';

export interface LegalResolution {
  id?: string;
  source: 'CENDOJ' | 'TEAC' | 'DGT' | 'HACIENDA' | 'JCCA_EXTREMADURA' | 'CCCP_ANDALUCIA' | string;
  kind?: string;
  title: string;
  type: 'CENDOJ' | 'TEAC' | 'DGT' | 'HACIENDA' | 'JCCA_EXTREMADURA' | 'CCCP_ANDALUCIA' | string;
  reference?: string;
  number: string;
  date?: string;
  court: string;
  summary: string;
  hechos?: string;
  antecedentesJuridicos?: string;
  fundamentos?: string;
  appliedLegislation?: string[];
  link: string;
  officialUrl?: string;
  sourceUrl?: string;
  topicTags?: string[];
  corpusOrigin?: string;
}

export interface LegalSearchParams {
  sources?: string[];
  kinds?: string[];
  jurisdiction?: 'civil' | 'penal' | 'contencioso' | 'social' | 'militar' | 'all' | string;
  court?: string;
  kind?: string;
  ecli?: string;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  forceLive?: boolean;
}

async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || ''}`,
  };
}

export function extractLegislationFromText(text: string): string[] {
  if (!text) return [];
  const found: string[] = [];
  const matches = text.match(/\b(?:Art(?:[ií]culo)?\.?\s*\d+(?:\.\d+)?(?:\s+(?:de\s+la\s+|del\s+)?(?:LCSP|LGT|LPACAP|LRJSP|TRLHL|CE|Ley\s+\d+\/\d{4}))?|Ley\s+\d+\/\d{4}|LCSP|LGT|LPACAP|LRJSP|TRLHL|CE)\b/gi);
  if (matches) {
    for (const m of matches) {
      const cleanM = m.trim();
      if (cleanM && !found.includes(cleanM)) {
        found.push(cleanM);
      }
    }
  }
  return found.slice(0, 8);
}

function normalizeResolution(resolution: any): LegalResolution {
  const source = resolution.source || resolution.type || 'DGT';
  const officialUrl = resolution.officialUrl || resolution.official_url || resolution.link || '';
  const sourceUrl = resolution.sourceUrl || resolution.source_url || '';
  const topicTags = Array.isArray(resolution.topicTags || resolution.topic_tags)
    ? (resolution.topicTags || resolution.topic_tags)
    : [];

  const rawLegis = resolution.appliedLegislation || resolution.applied_legislation || resolution.articulos;
  let appliedLegislation: string[] = Array.isArray(rawLegis) ? rawLegis : [];

  if (appliedLegislation.length === 0) {
    const fullCombined = `${resolution.title || ''} ${resolution.summary || ''} ${resolution.hechos || ''} ${resolution.antecedentesJuridicos || resolution.fundamentos || ''}`;
    appliedLegislation = extractLegislationFromText(fullCombined);
  }

  return {
    id: resolution.id,
    source,
    kind: resolution.kind || '',
    title: resolution.title || '',
    type: resolution.type || source,
    reference: resolution.reference || resolution.number || '',
    number: resolution.number || resolution.reference || '',
    date: resolution.date || resolution.resolution_date || '',
    court: resolution.court || '',
    summary: resolution.summary || '',
    hechos: resolution.hechos || resolution.antecedentes_hecho || resolution.facts || '',
    antecedentesJuridicos: resolution.antecedentesJuridicos || resolution.antecedentes_juridicos || resolution.fundamentos || resolution.fundamentos_derecho || '',
    fundamentos: resolution.fundamentos || resolution.antecedentesJuridicos || resolution.antecedentes_juridicos || '',
    appliedLegislation,
    link: resolution.link || officialUrl || '',
    officialUrl,
    sourceUrl,
    topicTags,
    corpusOrigin: resolution.corpusOrigin || resolution.corpus_origin || '',
  };
}

export async function searchLegalDetailed(query: string, params: LegalSearchParams = {}): Promise<{
  results: LegalResolution[];
  source?: string;
  fallbackUsed?: boolean;
  localCount?: number;
  ingestedCount?: number;
  crawlerErrors?: string[];
}> {
  const headers = await getHeaders();
  const resp = await fetch(`${API_URL}/legal-search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, ...params }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to search: ${resp.status}`);
  }
  const data = await resp.json();
  return {
    results: Array.isArray(data.results) ? data.results.map(normalizeResolution) : [],
    source: data.source,
    fallbackUsed: data.fallbackUsed,
    localCount: data.localCount,
    ingestedCount: data.ingestedCount,
    crawlerErrors: Array.isArray(data.crawlerErrors) ? data.crawlerErrors : [],
  };
}

export async function searchLegal(query: string, params: LegalSearchParams = {}): Promise<LegalResolution[]> {
  const response = await searchLegalDetailed(query, params);
  return response.results;
}

export async function compareResolutions(resolutions: LegalResolution[]): Promise<string> {
  const headers = await getHeaders();
  const resp = await fetch(`${API_URL}/legal-compare`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ resolutions }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to compare: ${resp.status}`);
  }
  const data = await resp.json();
  return data.comparison;
}

export async function summarizeResolutions(resolutions: LegalResolution[]): Promise<string> {
  const headers = await getHeaders();
  const resp = await fetch(`${API_URL}/legal-summarize`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ resolutions }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to summarize: ${resp.status}`);
  }
  const data = await resp.json();
  return data.summary;
}

export async function ingestLegalResolutions(resolutions: LegalResolution[], corpusOrigin = 'manual'): Promise<number> {
  const headers = await getHeaders();
  const resp = await fetch(`${API_URL}/legal-ingest`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ resolutions, corpusOrigin }),
  });
  if (!resp.ok) {
    throw new Error(`Failed to ingest: ${resp.status}`);
  }
  const data = await resp.json();
  return data.ingested || 0;
}
